import { useMemo, useState } from "react";
import { useLang } from "../LanguageContext";
import { translations } from "../data/translations";
import { chenbroProducts } from "../data/chenbroProducts";
import ProductCard from "./ProductCard";
import { motion } from "framer-motion";
import { Server, HardDrive, Cpu } from "lucide-react";

const INITIAL_VISIBLE = 12;

export default function ProductsSection() {
  const { lang } = useLang();
  const t = translations[lang];

  const [filter, setFilter] = useState("all");
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  const categories = [
    { key: "all", label: t.products?.all || "Alle", icon: Server },
    { key: "rackmount", label: t.products?.rackmount || "Rackmount", icon: HardDrive },
    { key: "tower", label: t.products?.tower || "Tower", icon: Cpu }
  ];

  const filteredProducts = useMemo(() => {
    if (filter === "all") return chenbroProducts;

    return chenbroProducts.filter((p) => {
      const cat = p.category?.toLowerCase() || "";

      if (filter === "rackmount") return cat.includes("rackmount");
      if (filter === "tower") return cat.includes("tower");

      return true;
    });
  }, [filter]);

  const visibleProducts = filteredProducts.slice(0, visibleCount);
  const hasMore = visibleCount < filteredProducts.length;

  const handleFilterChange = (nextFilter) => {
    setFilter(nextFilter);
    setVisibleCount(INITIAL_VISIBLE);
  };

  return (
    <section id="products" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-brand-blue mb-4">
            {t.products?.title || "Produkte"}
          </h2>

          <p className="text-gray-600 max-w-3xl mx-auto">
            {t.products?.subtitleSystems ||
              "Server chassis and platform solutions for professional IT infrastructures"}
          </p>
        </motion.div>

        <div className="flex justify-center mb-6">
          <div className="inline-flex bg-white border border-gray-200 rounded-full p-1 shadow-sm">
            <span className="px-4 py-2 text-sm font-semibold text-brand-blue">
              {t.products?.brandChenbro || "Chenbro"}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-8">
          {categories.map((cat) => (
            <button
              key={cat.key}
              onClick={() => handleFilterChange(cat.key)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                filter === cat.key
                  ? "bg-brand-blue text-white shadow-md"
                  : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200 shadow-sm"
              }`}
            >
              <cat.icon className="w-4 h-4" />
              {cat.label}
            </button>
          ))}
        </div>

        <div className="text-center mb-6 text-sm text-gray-500">
          {filteredProducts.length}{" "}
          {filteredProducts.length === 1
            ? t.products?.resultSingle || "Produkt"
            : t.products?.results || "Produkte"}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {visibleProducts.map((product, idx) => (
            <ProductCard key={product.id} product={product} index={idx} />
          ))}
        </div>

        {hasMore && (
          <div className="flex justify-center mt-10">
            <button
              onClick={() => setVisibleCount((prev) => prev + INITIAL_VISIBLE)}
              className="px-6 py-3 rounded-full bg-brand-blue text-white font-medium shadow hover:opacity-90 transition"
            >
              {t.products?.showMore || "Mehr anzeigen"}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
